import { Paper, Popover, SxProps, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from "react";
import {
  ButtonStyle,
  getButtonSectionStyle,
  MessageStyle,
  NameSectionBlinkStyle,
  NameSectionContainerStyle,
  NameSectionDefaultStyle,
  NumberSectionContainerStyle,
  NumberSectionStyle,
  PlateContainerStyle,
  PopoverStyle,
  SiteJobSectionContainerStyle,
  SiteJobSectionStyle,
  SubBoardContainerStyle,
  SubBoardTitleStyle
} from "./styled";

export interface IBoardPlateProps {
  isSubBoard?: boolean,
  // A switch to determine whether to display buttons 
  // or site job info in the board plate
  showSiteJob?: boolean,
  siteJob?: string,
  plateNumber: string,
  plateName: string | null,
  plateColor: string | null,
  isPlugin: boolean,
  isCallback: boolean,
  text: string,
  buttonImages: string[],
  blink: boolean,
  shouldHandlePluginAndCallback?: boolean,
  handlePlateNameClick?: (event: React.MouseEvent<HTMLDivElement>) => void,
  maxButtonImages?: number
}

const NumberSection = ({
  plateNumber,
  isPlugin,
  isCallback,
  shouldHandlePluginAndCallback,
  text
}: Pick<IBoardPlateProps, "plateNumber" | "isPlugin" | "isCallback" | "shouldHandlePluginAndCallback" | "text">) => {
  const theme = useTheme();
  const [message, setMessage] = useState(text);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessage(text);
  }, [text]);

  let backgroundColor = "transparent";
  let borderColor = "transparent";
  let border = 2;
  let dataAutomation = null;
  if (shouldHandlePluginAndCallback && isCallback && isPlugin) {
    backgroundColor = theme.palette.green[400];
    borderColor = theme.palette.red[200];
    dataAutomation = "plugged-in-and-called-back-square"
  } else if (isPlugin) {
    backgroundColor = theme.palette.green[400];
    borderColor = theme.palette.common.black;
    dataAutomation = "plugged-in-square"
  } else if (isCallback) {
    backgroundColor = theme.palette.red[200];
    borderColor = theme.palette.common.black;
    dataAutomation = "called-back-square"
  }
  const style = { ...NumberSectionStyle, border, backgroundColor, borderColor };

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!message) return;
    setAnchorEl(event.currentTarget);
  }

  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  return (
    <Box
      sx={NumberSectionContainerStyle}
    >
      <Paper
        elevation={0}
        square
        sx={style}
        data-automation={dataAutomation}
        onClick={handleOpen}
      >
        {plateNumber}
      </Paper>
      {message && <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={PopoverStyle}
      >
        <Typography sx={MessageStyle}>{message}</Typography>
      </Popover>
      }
    </Box>
  );
}

const NameSection = ({
  plateName,
  plateColor,
  blink,
  handlePlateNameClick
}: Pick<IBoardPlateProps, "plateName" | "plateColor" | "blink" | "handlePlateNameClick">) => {
  const theme = useTheme();
  let style: SxProps = { ...NameSectionDefaultStyle };
  let containerStyle: SxProps = { ...NameSectionContainerStyle };
  if (plateColor) {
    if (blink) {
      containerStyle = {
        ...containerStyle,
        ...NameSectionBlinkStyle(theme.palette.common.black, plateColor)
      };
    } else {
      containerStyle = {
        ...containerStyle,
        borderColor: plateColor,
        backgroundColor: plateColor
      };
    }
  }
  return (
    <Box
      sx={containerStyle}
      onClick={handlePlateNameClick}
    >
      <Paper
        elevation={0}
        square
        sx={style}
        data-automation={blink ? "current-user-tag" : null}
      >
        {plateName}
      </Paper>
    </Box>
  )
}

const ButtonSection = ({ buttonImages, maxButtonImages = 3 }: Pick<IBoardPlateProps, "buttonImages" | "maxButtonImages">) => {
  const [buttons, setButtons] = useState(buttonImages);

  useEffect(() => {
    setButtons(buttonImages);
  }, [buttonImages]);

  return (
    <Box sx={() => getButtonSectionStyle(maxButtonImages)}>
      {buttons && buttons.length > 0 &&
        // display at most 3 buttons
        buttons.slice(0, 3).map((button, i) => {
          const [alt] = button.split('.');
          return (
            <Box key={alt} sx={ButtonStyle} >
              <img src={`/assets/images/buttons/${button}`} alt={alt} />
            </Box>
          )
        })
      }
    </Box >
  );
}

const SiteJobSection = ({ siteJob }: Pick<IBoardPlateProps, "siteJob">) => {
  return <Box sx={SiteJobSectionContainerStyle}>
    <Paper
      elevation={0}
      square
      sx={SiteJobSectionStyle}
    >
      {siteJob}
    </Paper>
  </Box>
}

export const BoardPlate = ({
  showSiteJob,
  siteJob,
  isSubBoard,
  plateNumber,
  plateName,
  plateColor,
  isPlugin,
  isCallback,
  text,
  buttonImages,
  blink = false,
  shouldHandlePluginAndCallback = false,
  handlePlateNameClick,
  maxButtonImages
}: IBoardPlateProps) => {
  return (
    isSubBoard ? (
      <Box sx={SubBoardContainerStyle} data-automation="sub-board-lbl">
        <Typography sx={SubBoardTitleStyle}>
          {plateName}
        </Typography>
      </Box>
    ) : (
      <Box sx={PlateContainerStyle} data-automation="board-plate" id={buttonImages.join('-')}>
        {showSiteJob ? (
          <SiteJobSection siteJob={siteJob} />
        ) : (
          <ButtonSection
            maxButtonImages={maxButtonImages}
            buttonImages={buttonImages}
          />
        )}
        <NumberSection
          isPlugin={isPlugin}
          isCallback={isCallback}
          plateNumber={plateNumber}
          shouldHandlePluginAndCallback={shouldHandlePluginAndCallback}
          text={text}
        />
        <NameSection
          plateName={plateName}
          plateColor={plateColor}
          blink={blink}
          handlePlateNameClick={handlePlateNameClick}
        />
      </Box>
    )
  );
}